import React, { useMemo } from 'react';
import { BellRing, ArrowUpRight, Info, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import AllocationRec from './AllocationRec';
import AlertCard from './AlertCard';

const StrategyView = ({ operations, currentPrices, usdClpRate, formatCurrency, investorProfile }) => {
  // Calculate portfolio metrics
  const buyOperations = operations.filter(op => op.type === 'Buy');
  const sellOperations = operations.filter(op => op.type === 'Sell');

  // Current portfolio allocation by asset type
  const currentAllocation = useMemo(() => {
    const allocation = {};
    let totalValue = 0;

    buyOperations.forEach(op => {
      let currentPrice;
      if (op.assetType === 'USD Purchase') {
        currentPrice = usdClpRate;
      } else if (op.operationCurrency === 'USD') {
        const raw = currentPrices[op.name] || parseFloat(op.price || 0);
        currentPrice = raw * usdClpRate;
      } else {
        const fallback = parseFloat(op.price || 0);
        currentPrice = currentPrices[op.name] || fallback;
      }
      const value = parseFloat(op.quantity || 0) * currentPrice;
      const type = op.assetType || 'Other';

      if (!allocation[type]) allocation[type] = 0;
      allocation[type] += value;
      totalValue += value;
    });

    // Convert to percentages
    Object.keys(allocation).forEach(type => {
      allocation[type] = totalValue > 0 ? (allocation[type] / totalValue) * 100 : 0;
    });

    return { allocation, totalValue };
  }, [buyOperations, currentPrices]);

  // Target allocation based on investor profile
  const getTargetAllocation = (profile) => {
    switch (profile) {
      case 'conservative':
        return {
          'Liquidity': 30,
          'Time Deposit': 40,
          'FCI': 20,
          'ETF': 10,
          'Stock': 0
        };
      case 'aggressive':
        return {
          'Stock': 50,
          'ETF': 30,
          'FCI': 10,
          'Time Deposit': 5,
          'Liquidity': 5
        };
      case 'moderate':
      default:
        return {
          'ETF': 40,
          'Stock': 25,
          'FCI': 20,
          'Time Deposit': 10,
          'Liquidity': 5
        };
    }
  };

  const targetAllocation = getTargetAllocation(investorProfile);

  // Calculate alerts based on real data and investor profile
  const alerts = useMemo(() => {
    const alertList = [];
    const deviationThreshold = investorProfile === 'conservative' ? 3 : investorProfile === 'aggressive' ? 8 : 5;

    // Check allocation deviations based on profile
    Object.entries(targetAllocation).forEach(([type, targetPerc]) => {
      const currentPerc = currentAllocation.allocation[type] || 0;
      const deviation = Math.abs(currentPerc - targetPerc);

      if (deviation > deviationThreshold) {
        const severity = deviation > (deviationThreshold * 2) ? 'error' : 'warning';
        alertList.push({
          title: `${type} Allocation ${deviation > 0 ? 'Deviation' : 'Opportunity'}`,
          desc: `${type} allocation is ${currentPerc.toFixed(1)}% (Target: ${targetPerc}%). ${deviation > 0 ? 'Consider rebalancing to match your risk profile.' : 'Good alignment with your investment strategy.'}`,
          type: severity
        });
      }
    });

    // Profile-specific alerts
    if (investorProfile === 'conservative') {
      // Conservative alerts
      const stockAllocation = currentAllocation.allocation['Stock'] || 0;
      if (stockAllocation > 5) {
        alertList.push({
          title: 'High Risk Exposure',
          desc: 'Your portfolio has significant stock exposure, which may not align with your conservative risk tolerance.',
          type: 'warning'
        });
      }
    } else if (investorProfile === 'aggressive') {
      // Aggressive alerts
      const conservativeAssets = (currentAllocation.allocation['Liquidity'] || 0) + (currentAllocation.allocation['Time Deposit'] || 0);
      if (conservativeAssets > 15) {
        alertList.push({
          title: 'Conservative Assets Too High',
          desc: 'Consider reducing conservative assets to increase growth potential according to your aggressive profile.',
          type: 'info'
        });
      }
    }

    // Check for high concentration in single assets
    const maxAllocation = Math.max(...Object.values(currentAllocation.allocation));
    const concentrationThreshold = investorProfile === 'conservative' ? 40 : investorProfile === 'aggressive' ? 60 : 50;

    if (maxAllocation > concentrationThreshold) {
      alertList.push({
        title: 'Portfolio Concentration Risk',
        desc: `One asset type represents more than ${concentrationThreshold}% of your portfolio. Consider diversification.`,
        type: 'warning'
      });
    }

    // Check USD/CLP rate opportunity based on profile
    const usdClpChange = ((usdClpRate - 950) / 950) * 100;
    const fxThreshold = investorProfile === 'conservative' ? 1 : investorProfile === 'aggressive' ? 3 : 2;

    if (Math.abs(usdClpChange) > fxThreshold) {
      alertList.push({
        title: 'FX Opportunity',
        desc: `USD/CLP is ${usdClpChange > 0 ? 'above' : 'below'} average by ${Math.abs(usdClpChange).toFixed(1)}%. ${
          usdClpChange < 0 ? 'Consider USD purchases for growth.' : 'Consider CLP deposits for stability.'
        }`,
        type: usdClpChange < 0 ? (investorProfile === 'aggressive' ? 'success' : 'info') : 'info'
      });
    }

    // Check for operations without diversification based on profile
    const uniqueAssets = new Set(buyOperations.map(op => op.name));
    const minAssets = investorProfile === 'conservative' ? 2 : investorProfile === 'aggressive' ? 4 : 3;

    if (uniqueAssets.size < minAssets && buyOperations.length > 0) {
      alertList.push({
        title: 'Low Diversification',
        desc: `Your portfolio has fewer than ${minAssets} different assets. Consider adding more diversification for your ${investorProfile} profile.`,
        type: 'warning'
      });
    }

    return alertList;
  }, [currentAllocation, targetAllocation, usdClpRate, buyOperations, investorProfile]);

  // Determine investor profile based on selected profile
  const investorProfileData = useMemo(() => {
    switch (investorProfile) {
      case 'conservative':
        return {
          name: 'Conservative Investor',
          yield: '4-6%',
          description: 'Focus on capital preservation with low risk tolerance.',
          riskLevel: 'Low',
          primaryFocus: 'Preservation'
        };
      case 'aggressive':
        return {
          name: 'Aggressive Investor',
          yield: '8-12%',
          description: 'Focus on growth with higher risk tolerance.',
          riskLevel: 'High',
          primaryFocus: 'Growth'
        };
      case 'moderate':
      default:
        return {
          name: 'Moderate Investor',
          yield: '6-8%',
          description: 'Balance between growth and risk management.',
          riskLevel: 'Medium',
          primaryFocus: 'Balance'
        };
    }
  }, [investorProfile]);

  // Generate dynamic recommendations - show all target allocations
  const recommendations = useMemo(() => {
    const recs = [];

    Object.entries(targetAllocation).forEach(([type, targetPerc]) => {
      const currentPerc = currentAllocation.allocation[type] || 0;
      const diff = targetPerc - currentPerc;

      recs.push({
        type,
        percentage: targetPerc,
        current: currentPerc,
        action: diff > 0 ? 'increase' : 'decrease',
        amount: Math.abs(diff),
        needsAdjustment: Math.abs(diff) > 5
      });
    });

    return recs;
  }, [currentAllocation, targetAllocation]);

  // Alert action handlers
  const handleAlertAction = (alert) => {
    switch (alert.type) {
      case 'error':
        window.alert(`Fixing: ${alert.title}\n${alert.desc}\n\nThis would typically open a rebalancing wizard or execute automated adjustments.`);
        break;
      case 'warning':
        window.alert(`Addressing: ${alert.title}\n${alert.desc}\n\nThis would typically show detailed analysis or suggested actions.`);
        break;
      case 'success':
        window.alert(`Executing: ${alert.title}\n${alert.desc}\n\nThis would typically execute the opportunity or show execution details.`);
        break;
      case 'info':
        window.alert(`Info: ${alert.title}\n${alert.desc}\n\nThis would typically show more detailed information or analysis.`);
        break;
      default:
        window.alert(`Action for: ${alert.title}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-10">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5 bg-gradient-to-br from-[#041627] to-[#1a2b3c] p-5 md:p-8 rounded-2xl text-white shadow-xl flex flex-col justify-between">
          <div>
            <span className="bg-[#6bfe9c] text-[#005228] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Active Profile</span>
            <h2 className="text-3xl font-bold mt-4 mb-2 font-headline">{investorProfileData.name}</h2>
            <p className="text-slate-400 text-sm leading-relaxed">{investorProfileData.description}</p>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-8">
             <div className="bg-white/5 p-4 rounded-xl">
               <p className="text-[10px] uppercase opacity-50 mb-1">Target Yield</p>
               <p className="text-xl font-bold">{investorProfileData.yield} APY</p>
             </div>
             <div className="bg-white/5 p-4 rounded-xl">
               <p className="text-[10px] uppercase opacity-50 mb-1">Portfolio Value</p>
               <p className="text-xl font-bold text-[#6bfe9c]">{formatCurrency(currentAllocation.totalValue)}</p>
             </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 bg-white p-5 md:p-8 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex justify-between items-start mb-6">
            <h3 className="font-bold text-lg text-[#041627]">Monthly Recommendation</h3>
            <ArrowUpRight className="text-[#6bfe9c]" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
             {recommendations.map((rec, index) => (
               <AllocationRec
                 key={index}
                 label={rec.type}
                 perc={`${rec.percentage}%`}
                 color={rec.needsAdjustment
                   ? (rec.action === 'increase' ? 'bg-[#6bfe9c]' : 'bg-red-500')
                   : 'bg-slate-300'
                 }
                 currentPerc={`${rec.current.toFixed(1)}%`}
               />
             ))}
          </div>
          <div className="mt-8 p-4 bg-slate-50 border border-dashed border-slate-200 rounded-lg flex items-center justify-between">
             <div className="flex items-center gap-3">
               <Info size={16} className="text-[#041627]" />
               <p className="text-xs text-slate-500">
                 {recommendations.some(rec => rec.needsAdjustment)
                   ? `Consider adjusting allocations that deviate from targets.`
                   : 'Your current allocation matches recommended targets.'
                 }
               </p>
             </div>
             <button className="text-xs font-bold text-[#041627] underline">Execute Plan</button>
          </div>
        </div>
      </div>

      <div className="space-y-6">
         <h3 className="text-xl font-bold flex items-center gap-2">
           <BellRing size={20} className="text-red-500" /> Priority Alerts ({alerts.length})
         </h3>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {alerts.length > 0 ? (
              alerts.map((alert, index) => (
                <AlertCard
                  key={index}
                  title={alert.title}
                  desc={alert.desc}
                  type={alert.type}
                  onAction={() => handleAlertAction(alert)}
                />
              ))
            ) : (
              <div className="col-span-2 text-center py-12 text-slate-500">
                <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
                <h4 className="font-bold text-lg mb-2">All Clear!</h4>
                <p>Your portfolio is well-maintained. No alerts at this time.</p>
              </div>
            )}
         </div>
      </div>
    </div>
  );
};

export default StrategyView;